import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TableWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import { applyContentItemOverrides } from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { GetSocialListeningMyBrandAnalyticsTrendingConversationsService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-trending-conversations.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

// Categorical bar palette — one color per topic row, in order, mirroring the
// LOLLY report mockup (Figma 13302:101718). Cycles if there are >10 rows.
const BAR_COLORS = [
	'#2872e1',
	'#e94f6a',
	'#2bb673',
	'#a98cf0',
	'#f5c451',
	'#6cd6c8',
	'#f0a3c6',
	'#5b8def',
	'#9aa7c7',
	'#cbb892'
];

interface ConversationBar {
	context: string;
	count: number;
	color: string;
	widthPct: number;
}

@Component({
	standalone: true,
	selector: 'app-trending-conversations-slide',
	templateUrl: './trending-conversations-slide.component.html',
	styleUrls: ['./trending-conversations-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		DecimalPipe,
		IconComponent,
		TypographyComponent,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class TrendingConversationsSlideComponent
	implements OnChanges, OnDestroy
{
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = '#2872e1';
	tableAccentColor = '#2872e1';
	dateLabel = '';
	summaryText = '';
	bars: ConversationBar[] = [];
	topicCount = '';
	topicCountLabel = '';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'trending_conversations',
		null,
		null
	);

	get isEmpty(): boolean {
		return !this.bars.length;
	}

	private destroy$ = new Subject<void>();
	private cachedConversations: Array<{
		context: string;
		count: number;
	}> | null = null;
	private cacheKey: string | null = null;

	constructor(
		private analyticsService: GetSocialListeningMyBrandAnalyticsTrendingConversationsService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedConversations && this.cacheKey === currentCacheKey) {
			this.applyConversations(this.cachedConversations);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(conversations => {
				this.cachedConversations = conversations;
				this.cacheKey = currentCacheKey;
				this.applyConversations(conversations);
			});
	}

	private applyConversations(
		conversations: Array<{ context: string; count: number }>
	): void {
		const rawSlide = this.buildSlide(conversations);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(
		conversations: Array<{ context: string; count: number }>
	): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'trending_conversations',
			title: this.item.title || 'Trending Conversations',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Trending Conversations',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: {
						text:
							this.item.description ||
							'Key conversations and themes related to your monitored keywords.'
					}
				},
				{
					id: `${prefix}_table`,
					kind: 'table',
					visible: true,
					content: {
						dataSourceKey: 'trending_conversations_table',
						accentColor: '#2872e1',
						columns: [
							{
								key: 'conversation',
								label: 'Top Trending Conversations'
							},
							{ key: 'mentions', label: 'Mentions' }
						],
						rows: conversations.map(c => ({
							conversation: c.context,
							mentions: String(c.count)
						}))
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			w => w.kind === 'title' && !hidden.has(w.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			w => w.kind === 'date_range' && !hidden.has(w.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			w => w.kind === 'summary_text' && !hidden.has(w.id)
		) as SummaryTextWidget | undefined;
		const tableWidget = slide.widgets.find(
			w => w.kind === 'table' && !hidden.has(w.id)
		) as TableWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.tableAccentColor = tableWidget?.content.accentColor ?? '#2872e1';
		this.dateLabel = dateWidget?.content.text ?? this.dateRange.label;
		this.summaryText = summaryWidget?.content.text ?? '';

		const rows = (tableWidget?.content.rows ?? []).map(row => ({
			context: row['conversation'] ?? '',
			count: parseInt(row['mentions'] ?? '0', 10)
		}));

		this.buildBars(rows);
	}

	private buildBars(rows: { context: string; count: number }[]): void {
		const maxVal = rows.reduce((m, r) => Math.max(m, r.count), 0);

		this.bars = rows.slice(0, 10).map((r, i) => ({
			context: r.context,
			count: r.count,
			color: BAR_COLORS[i % BAR_COLORS.length],
			// TRAP: 85, not 100 — the value label follows the longest bar and needs
			// room inside the slide (top-sites-by-visits-slide does the same).
			widthPct: maxVal > 0 ? (r.count / maxVal) * 85 : 0
		}));

		// The same ten topics, each in the colour its bar has here.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: this.bars.map(bar => bar.context),
				series: [
					{
						key: 'mentions',
						label: 'Mentions',
						values: this.bars.map(bar => bar.count)
					}
				],
				labelColors: this.bars.map(bar => bar.color),
				labelColumn: 'Topic'
			})
		);

		// KPI callout — number of distinct topic clusters surfaced.
		if (this.bars.length) {
			this.topicCount = String(this.bars.length);
			this.topicCountLabel = 'Major topics';
		} else {
			this.topicCount = '';
			this.topicCountLabel = '';
		}
	}
}
